"use client";

import { useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
gsap.registerPlugin(useGSAP, ScrollTrigger);
export default function Consultation() {
  const container = useRef<HTMLDivElement>(null);
  useGSAP(() => {
    const mm = gsap.matchMedia();
    mm.add({
      reduced: "(prefers-reduced-motion: reduce)",
      normal: "(prefers-reduced-motion: no-preference)"
    }, context => {
      const conditions = context.conditions as {
        reduced: boolean;
        normal: boolean;
      };
      if (conditions.reduced) {
        gsap.set(".rsv-line", {
          yPercent: 0,
          opacity: 1
        });
        gsap.set(".rsv-marker-path", {
          strokeDashoffset: 0
        });
        gsap.set(".rsv-fade", {
          opacity: 1,
          y: 0
        });
        return;
      }
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: container.current,
          start: "top 75%",
          once: true
        }
      });
      tl.from(".rsv-eyebrow", {
        opacity: 0,
        y: 12,
        duration: 0.6,
        ease: "power2.out"
      });
      tl.from(".rsv-line", {
        yPercent: 115,
        opacity: 0,
        duration: 0.9,
        stagger: 0.09,
        ease: "cubic-bezier(0.2, 0.8, 0.2, 1)"
      }, "-=0.3");
      const path = container.current?.querySelector(".rsv-marker-path") as SVGPathElement | null;
      if (path) {
        const length = path.getTotalLength();
        gsap.set(path, {
          strokeDasharray: length,
          strokeDashoffset: length
        });
        tl.to(path, {
          strokeDashoffset: 0,
          duration: 0.7,
          ease: "cubic-bezier(0.65, 0, 0.35, 1)"
        }, "+=0.4");
      }
      tl.from(".rsv-body", {
        opacity: 0,
        y: 20,
        duration: 0.7,
        ease: "power3.out"
      }, "-=0.3");
      tl.from(".rsv-cta", {
        opacity: 0,
        y: 20,
        duration: 0.7,
        ease: "power3.out"
      }, "-=0.4");
      tl.from(".rsv-caption", {
        opacity: 0,
        y: 12,
        duration: 0.6,
        ease: "power2.out"
      }, "-=0.4");
    });
  }, {
    scope: container
  });
  return <section data-section-id="23036" id="consultationcta" data-nav="light" className="relative bg-background text-foreground py-40 md:py-56 overflow-hidden">
      <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-20">
        <div className="w-full" aria-hidden="true">
          <div className="mx-auto mb-24 h-px w-full bg-border" />
        </div>

        <div ref={container} className="mx-auto flex flex-col items-center text-center">
          <p className="rsv-eyebrow font-sans text-[13px] sm:text-sm font-medium uppercase text-muted-foreground" style={{
          letterSpacing: "0.16em"
        }}>
            Start Your Growth
          </p>

          <h1 className="rsv-headline mt-16 font-serif text-foreground mx-auto max-w-[16ch]" style={{
          fontSize: "clamp(2.25rem, 4.5vw, 3.75rem)",
          fontWeight: 340,
          letterSpacing: "-0.035em",
          lineHeight: 0.94
        }}>
            <span className="block overflow-hidden">
              <span className="rsv-line inline-block">Book your</span>
            </span>
            <span className="block overflow-hidden">
              <span className="rsv-line inline-block relative">
                <span className="italic font-serif relative inline-block">
                  strategic
                  <svg className="absolute left-0 right-0 -bottom-2 md:-bottom-3 w-full pointer-events-none" viewBox="0 0 300 18" preserveAspectRatio="none" aria-hidden="true" style={{
                  height: "0.28em"
                }}>
                    <path className="rsv-marker-path" d="M4 11 C 60 4, 130 15, 200 8 S 285 6, 296 10" fill="none" stroke="hsl(340 40% 90%)" strokeWidth="8" strokeLinecap="round" />
                  </svg>
                </span>
              </span>
            </span>
            <span className="block overflow-hidden">
              <span className="rsv-line inline-block">consultation.</span>
            </span>
          </h1>

          <p className="rsv-body rsv-fade mt-14 max-w-[36ch] font-sans text-muted-foreground" style={{
          fontSize: "clamp(1.1875rem, 1.6vw, 1.4rem)",
          lineHeight: 1.55,
          letterSpacing: "-0.005em"
        }}>
            I work directly with you to audit your site and build a roadmap for sustainable search growth.
          </p>

          <div className="rsv-cta rsv-fade mt-16">
            <Button asChild className="h-auto rounded-lg bg-primary text-primary-foreground hover:bg-transparent hover:text-primary border-[1.5px] border-primary transition-all duration-300 font-sans font-medium text-base md:text-lg" style={{
            padding: "1.25rem 2.25rem",
            letterSpacing: "0.01em"
          }}>
              <Link to="/ContactPage">Book Free Session</Link>
            </Button>
          </div>

          <p className="rsv-caption rsv-fade mt-6 font-sans text-muted-foreground" style={{
          fontSize: "clamp(0.875rem, 0.95vw, 0.9375rem)",
          letterSpacing: "0.02em",
          lineHeight: 1.4
        }}>
            Or email me directly —{" "}
            <Link to="mailto:contact@ye-consultancy.com" className="group relative inline-block text-foreground">
              <span>contact@ye-consultancy.com</span>
              <span className="absolute left-0 right-0 -bottom-0.5 h-px bg-foreground transition-opacity duration-200 group-hover:opacity-0" aria-hidden="true" />
              <svg className="absolute left-0 right-0 -bottom-1 w-full opacity-0 transition-opacity duration-200 group-hover:opacity-100 pointer-events-none" viewBox="0 0 200 8" preserveAspectRatio="none" aria-hidden="true" style={{
              height: "0.5em"
            }}>
                <path d="M2 5 C 40 2, 90 7, 140 3 S 190 4, 198 5" fill="none" stroke="hsl(340 40% 90%)" strokeWidth="3" strokeLinecap="round" />
              </svg>
            </Link>
          </p>
        </div>
      </div>
    </section>;
}